/**
 * Typed wrappers around the chores Tier-2 wasm entry points.
 * Mirrors `ios/Sources/Core/Tier2Wasm/Tier2.swift` so the same
 * JSON-in / JSON-out shapes work on both clients — when the Go
 * shim grows a new export, the iOS + web wrappers can land in
 * lockstep.
 */

import { WasmHost, WasmHostError } from './WasmHost';

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8');

export type Tier2ErrorKind = 'host' | 'wasm' | 'decode';

export class Tier2Error extends Error {
  constructor(
    public readonly kind: Tier2ErrorKind,
    message: string,
    public readonly category?: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'Tier2Error';
  }
}

export interface Tier2Defaults {
  vanish_default_time: string;
  day_start_hour_fallback: number;
  default_hour_fallback: number;
}

export class Tier2 {
  constructor(private readonly host: WasmHost) {}

  /**
   * Convenience: load core.wasm from a URL and return a Tier2.
   * The URL is normally `/core.wasm` (served from web/public/);
   * tests can point at a local path or in-memory bytes via the
   * `WasmHost.loadFromBytes` overload.
   */
  static async loadFromUrl(url: string): Promise<Tier2> {
    const host = await WasmHost.loadFromUrl(url);
    return new Tier2(host);
  }

  /**
   * GetDefaults — returns the cross-platform defaults baked into
   * the bundled core.wasm. Single source of truth so the web
   * client stays in lockstep with Go's `internal/core/defaults.go`
   * and iOS's AppDefaults.
   */
  getDefaults(): Tier2Defaults {
    const out = this.invokeJSON('GetDefaults', '{}');
    return out as Tier2Defaults;
  }

  private invokeJSON(name: string, inputJSON: string): unknown {
    let raw: Uint8Array;
    try {
      raw = this.host.invoke(name, encoder.encode(inputJSON));
    } catch (e) {
      if (e instanceof WasmHostError) {
        throw new Tier2Error('host', e.message, undefined, e);
      }
      throw e;
    }
    const text = decoder.decode(raw);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      throw new Tier2Error('decode', `decode ${name}: ${(e as Error).message}`, undefined, e);
    }
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      const body = (parsed as { error?: { code?: string; message?: string } }).error;
      if (body) {
        throw new Tier2Error('wasm', body.message ?? 'wasm error', body.code);
      }
    }
    return parsed;
  }
}
